/**
 * MediaViewer - fullscreen media overlay with pinch-zoom + pan.
 *
 * Supports two-finger pinch to zoom and one-finger drag to pan while zoomed.
 * Double-tap toggles between fit and 2.5x. Tapping the dimmed backdrop (when
 * not zoomed) closes the viewer. Videos play with native controls.
 *
 * The transform is kept in component state and applied via a CSS transform so
 * gestures stay smooth (no layout thrash).
 */
import { useState, useRef, useCallback } from 'preact/hooks';

const MAX_SCALE = 5;
const MIN_SCALE = 1;

const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

export function MediaViewer({ media, onClose }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const gesture = useRef(null);
  const lastTap = useRef(0);

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  const onTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 2) {
        gesture.current = {
          mode: 'pinch',
          startDist: dist(e.touches[0], e.touches[1]),
          startScale: scale,
        };
      } else if (e.touches.length === 1) {
        gesture.current = {
          mode: 'pan',
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startTx: tx,
          startTy: ty,
        };
      }
    },
    [scale, tx, ty],
  );

  const onTouchMove = useCallback((e) => {
    const g = gesture.current;
    if (!g) return;
    if (g.mode === 'pinch' && e.touches.length === 2) {
      e.preventDefault();
      const next = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, (dist(e.touches[0], e.touches[1]) / g.startDist) * g.startScale),
      );
      setScale(next);
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
    } else if (g.mode === 'pan' && e.touches.length === 1) {
      e.preventDefault();
      setTx(g.startTx + (e.touches[0].clientX - g.startX));
      setTy(g.startTy + (e.touches[0].clientY - g.startY));
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    gesture.current = null;
  }, []);

  const onImgClick = useCallback(() => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      // double-tap toggle zoom
      if (scale > 1) reset();
      else setScale(2.5);
    }
    lastTap.current = now;
  }, [scale, reset]);

  if (!media) return null;

  const transform = `translate(${tx}px, ${ty}px) scale(${scale})`;

  return (
    <div class="viewer" onClick={(e) => e.target.classList.contains('viewer') && scale === 1 && onClose()}>
      <button type="button" class="viewer__close" onClick={onClose} aria-label="Close">
        ✕
      </button>
      <div
        class="viewer__stage"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {media.kind === 'video' ? (
          <video
            class="viewer__media"
            src={media.url}
            controls
            autoplay
            playsinline
            style={{ transform }}
          />
        ) : (
          <img
            class="viewer__media"
            src={media.url}
            alt=""
            style={{ transform }}
            onClick={onImgClick}
            draggable={false}
          />
        )}
      </div>
    </div>
  );
}
